import React, { useEffect, useState } from "react";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { fetchUpcomingServices } from "@services/upcomingServiceService";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { formatDate } from "@util/string";
import { UpcomingService } from "@models/UpcomingService";
import { useAuth } from "@context/auth-context";
import AvailableWithPlatformAccessButton from "@components/buttons/AvailableWithPlatformAccessButton/AvailableWithPlatformAccessButton";

const RESULTS_PER_PAGE = 12;
const DAYS_WINDOW = 60;

// Only show anniversaries for these milestone years
const MILESTONE_YEARS = [1, 3, 5, 10];

const AnniversariesPage: React.FC = () => {
    const { isSubscribed } = useAuth();
    const [currentPage, setCurrentPage] = useState(1);
    const [services, setServices] = useState<UpcomingService[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchUpcomingServices();
                setServices(data);
            } catch (err) {
                setError("Failed to fetch anniversaries.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    const today = new Date();
    const sixtyDaysFromNow = new Date();
    sixtyDaysFromNow.setDate(today.getDate() + DAYS_WINDOW);

    // Helper: check if a date's month/day is within next 60 days
    const isWithinNext60Days = (date: Date) => {
        const currentYear = today.getFullYear();
        if (date.getFullYear() >= currentYear) return false;

        const todayMidnight = new Date(currentYear, today.getMonth(), today.getDate());
        const sixtyDaysMidnight = new Date(currentYear, sixtyDaysFromNow.getMonth(), sixtyDaysFromNow.getDate());
        const anniversary = new Date(currentYear, date.getMonth(), date.getDate());

        return anniversary >= todayMidnight && anniversary <= sixtyDaysMidnight;
    };

    // Filter anniversaries
    const anniversaries = services.filter((s) => {
        if (!s.deathDate || !s.birthDate) return false;
        if (!isWithinNext60Days(s.deathDate)) return false;

        const currentYear = today.getFullYear();
        const years = currentYear - s.deathDate.getFullYear();

        return MILESTONE_YEARS.includes(years);
    });

    // Sort by upcoming anniversary date
    const sortedAnniversaries = anniversaries.sort((a, b) => {
        const currentYear = today.getFullYear();

        if (!a.deathDate && !b.deathDate) return 0;
        if (!a.deathDate) return 1;
        if (!b.deathDate) return -1;

        const annA = new Date(currentYear, a.deathDate.getMonth(), a.deathDate.getDate());
        const annB = new Date(currentYear, b.deathDate.getMonth(), b.deathDate.getDate());

        return annA.getTime() - annB.getTime();
    });

    const totalPages = Math.ceil(sortedAnniversaries.length / RESULTS_PER_PAGE);
    const paginatedData = sortedAnniversaries.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (!isSubscribed) {
            return (
                <div className={commonStyles.container}>
                    <AvailableWithPlatformAccessButton />
                </div>
            );
        }

        if (paginatedData.length === 0) {
            return (
                <div className={`${commonStyles.noContent} ${commonStyles.note}`}>
                    {`No milestone anniversaries in the next ${DAYS_WINDOW} days.`}
                </div>
            );
        }

        return (
            <div className={commonStyles.itemList}>
                {paginatedData.map((data) => {
                    const currentYear = today.getFullYear();
                    const years = currentYear - (data.deathDate?.getFullYear() ?? currentYear);

                    return (
                        <InfoCard
                            key={data.id}
                            title={`${data.firstName} ${data.lastName}`}
                            items={[
                                data.birthDate && data.deathDate
                                    ? `${formatDate(data.birthDate)} - ${formatDate(data.deathDate)}`
                                    : "Dates not available",
                                `${years} year${years === 1 ? "" : "s"} anniversary`
                            ]}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            <div className={commonStyles.headerContainer}>
                <h1>Upcoming Death Anniversaries</h1>
                {isSubscribed && (
                    <p className={commonStyles.note}>
                        Showing milestone anniversaries at 1, 3, 5 and 10 years.
                    </p>
                )}
            </div>
            {renderContent()}
            {totalPages > 1 && isSubscribed && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}
        </div>
    );
};

export default AnniversariesPage;
